/**
 * Data mappers for converting GraphQL Location types to legacy IStation interface
 *
 * These mappers ensure compatibility between the new GraphQL data structures
 * and existing component interfaces, allowing gradual migration.
 */

import type {
  IStation,
  IStationIcon,
  LocationStats,
} from '../../../../ts/interface/station.interface';
import type { IRate } from '../../../../ts/interface/rental.interface';
import type { IHardware } from '../../../../ts/interface/hardware.interface';
import type { LocationGraphQL, LocationRateGraphQL } from './useLocationsRelay';
import { buildRateOverview, formatRatePeriod } from '../../../../util/rateOverview';
import type {
  LocationDetailGraphQL,
  LocationRateDetailGraphQL,
  LocationHardwareGraphQL,
} from './useLocationDetailRelay';

// ============================================================================
// Rate Mappers
// ============================================================================

/**
 * Map GraphQL Rate to legacy IRate interface
 */
export function mapLocationRateToIRate(
  rate: LocationRateGraphQL | LocationRateDetailGraphQL
): IRate {
  return {
    id: rate.oldId ?? rate.id,
    gid: rate.id,
    old_id: rate.oldId ?? '',
    name: rate.name,
    credit_card_hold: rate.creditCardHold ?? null,
    minimum_wallet_deposit: rate.minimumWalletDeposit ?? null,
    hidden: rate.hidden ?? false,
    max_rentals: (rate as LocationRateDetailGraphQL).maxRentals ?? null,
    max_periods: rate.maxPeriods ?? null,
    active_on: (rate as LocationRateDetailGraphQL).activeOn ?? '',
    base_rate: rate.baseRate ?? 0,
    price_per_period: rate.pricePerPeriod ?? 0,
    stringDuration: formatRatePeriod(rate.periodDuration ?? 0),
    period_duration: rate.periodDuration ?? 0,
    free_time: rate.freeTime ?? 0,
    overview: buildRateOverview(rate),
    archived: rate.archivedOn ?? null,
    reservation: false, // Not available in GraphQL Rate type
    membership_required: rate.membershipRequired ?? false,
  };
}

/**
 * Map array of GraphQL rates to IRate array
 */
export function mapLocationRatesToIRates(
  rates: (LocationRateGraphQL | LocationRateDetailGraphQL)[] | null | undefined
): IRate[] {
  if (!rates) return [];
  return rates.map(mapLocationRateToIRate);
}

// ============================================================================
// Hardware Mappers
// ============================================================================

/**
 * Map GraphQL Hardware at location to partial IHardware interface
 */
export function mapLocationHardwareToIHardware(
  hardware: LocationHardwareGraphQL
): IHardware {
  return {
    id: hardware.id,
    gid: hardware.id,
    old_id: hardware.oldId ?? '',
    number: hardware.number ?? 0,
    label: hardware.label ?? null,
    mac: hardware.mac ?? '',
    imei: hardware.imei ?? '',
    state: hardware.state ?? 0,
    isAvailable: hardware.isAvailable ?? false,
    isActive: hardware.isActive ?? false,
    isOnline: hardware.isOnline ?? null,
    lastSeen: hardware.lastSeen ?? null,
    battery_percent: hardware.batteryPercent ?? null,
    battery_voltage: hardware.batteryVoltage ?? null,
    type_name: hardware.hardwareType?.name ?? '',
    hardware_type_name: hardware.hardwareType?.name ?? '',
    // Provide defaults for required IHardware fields
    name: `Unit ${hardware.number}`,
    qr_code: '',
    vendor_id: hardware.vendorId ?? '',
    vendor_serial: hardware.vendorSerial ?? '',
    vendor_qr_code: '',
    last_lat: null,
    last_lng: null,
    // Additional fields with defaults
    last_location: null,
    current_rental: null,
    total_rentals: 0,
    station_name: '',
    lock_state: null,
    configuration: '',
    system_info: null,
    hardware_product_version: null,
  } as IHardware;
}

/**
 * Map array of GraphQL hardware to IHardware array
 */
export function mapLocationHardwareToIHardwareArray(
  hardware: LocationHardwareGraphQL[] | null | undefined
): IHardware[] {
  if (!hardware) return [];
  return hardware.map(mapLocationHardwareToIHardware);
}

// ============================================================================
// Icon Mappers
// ============================================================================

/**
 * Map GraphQL Icon to legacy IStationIcon interface
 */
export function mapLocationIconToIStationIcon(
  icon: LocationGraphQL['icon'] | LocationDetailGraphQL['icon'],
  locationId?: string
): IStationIcon | undefined {
  if (!icon) return undefined;

  return {
    id: icon.id,
    gid: icon.id,
    approved_on: icon.approvedOn ?? '',
    comment: (icon as LocationDetailGraphQL['icon'])?.comment ?? null,
    created: null,
    type: icon.type ?? undefined,
    station_id: locationId,
    // URL will be constructed by the component using the icon ID
  };
}

// ============================================================================
// Location Mappers
// ============================================================================

/**
 * Convert GraphQL polygon coordinates to IStation format
 * GraphQL returns [[lng, lat], ...] but IStation may need [{lat, lng}, ...]
 */
function mapPolygonCoordinates(
  coords: ReadonlyArray<ReadonlyArray<number>> | null | undefined
): number[][] | { lat: number; lng: number }[] {
  if (!coords || coords.length === 0) return [];

  // Return as number[][] format (GraphQL format)
  return coords.map((point) => [...point]);
}

/**
 * Map a GraphQL Location to the legacy IStation interface for list views
 *
 * @param location - The GraphQL location data
 * @returns IStation compatible object
 */
export function mapLocationToIStation(location: LocationGraphQL): IStation {
  return {
    id: location.id,
    gid: location.id,
    old_id: location.oldId ?? '',
    name: location.name,
    state: location.state ?? 0,
    configuration: location.configuration ?? '',
    lat: location.lat?.toString() ?? undefined,
    lng: location.lng?.toString() ?? undefined,
    polygon_coordinates: mapPolygonCoordinates(location.polygonCoordinates),
    capacity: location.capacity ?? 0,
    radius: location.radius ?? null,
    zone_type: location.zoneType ?? '',
    slow_zone_speed: location.slowZoneSpeed ?? null,
    parking_credit: 0, // Not available on Location - comes from System
    reservations: location.reservations ?? false,
    maxReservations: location.maxReservations ?? 0,
    prevent_ending_rentals: location.preventEndingRentals ?? false,
    prevent_starting_rentals: location.preventStartingRentals ?? false,
    private: location.private ?? false,
    module: location.module ?? '',
    banner_id: location.bannerId ?? '',
    icon_id: location.iconId ?? '',
    available_hardware: location.availableHardwareCount,
    totalHardware: location.totalHardwareCount,
    totalRentableHardware: location.totalRentableHardwareCount,
    hardware_type: '', // Not available on Location - comes from System
    hardware_type_name: undefined, // Not available on Location - comes from System
    hardware: [], // Hardware not included in list query
    rates: mapLocationRatesToIRates(location.rates),
    icon: mapLocationIconToIStationIcon(location.icon, location.id),
    searchHardwareValue: '',
    system_info: location.system
      ? {
          id: location.system.id,
          name: location.system.name,
          old_id: location.system.oldId ?? '',
        }
      : undefined,
  };
}

/**
 * Map an array of GraphQL locations to IStation array
 */
export function mapLocationsToIStations(locations: LocationGraphQL[]): IStation[] {
  return locations.map(mapLocationToIStation);
}

/**
 * Map a GraphQL LocationDetail to the legacy IStation interface
 *
 * @param location - The GraphQL location detail data
 * @returns IStation compatible object with all fields populated
 */
export function mapLocationDetailToIStation(location: LocationDetailGraphQL): IStation {
  return {
    id: location.id,
    gid: location.id,
    old_id: location.oldId ?? '',
    name: location.name,
    state: location.state ?? 0,
    configuration: location.configuration ?? '',
    lat: location.lat?.toString() ?? undefined,
    lng: location.lng?.toString() ?? undefined,
    polygon_coordinates: mapPolygonCoordinates(location.polygonCoordinates),
    capacity: location.capacity ?? 0,
    radius: location.radius ?? null,
    zone_type: location.zoneType ?? '',
    slow_zone_speed: location.slowZoneSpeed ?? null,
    parking_credit: 0, // Not available on Location - comes from System
    reservations: location.reservations ?? false,
    maxReservations: location.maxReservations ?? 0,
    prevent_ending_rentals: location.preventEndingRentals ?? false,
    prevent_starting_rentals: location.preventStartingRentals ?? false,
    private: location.private ?? false,
    module: location.module ?? '',
    banner_id: location.bannerId ?? '',
    icon_id: location.iconId ?? '',
    available_hardware: location.availableHardwareCount,
    totalHardware: location.totalHardwareCount,
    totalRentableHardware: location.totalRentableHardwareCount,
    hardware_type: '', // Not available on Location - comes from System
    hardware_type_name: undefined, // Not available on Location - comes from System
    hardware: mapLocationHardwareToIHardwareArray(location.hardware),
    rates: mapLocationRatesToIRates(location.rates),
    icon: mapLocationIconToIStationIcon(location.icon, location.id),
    searchHardwareValue: '',
    system_info: location.system
      ? {
          id: location.system.id,
          name: location.system.name,
          old_id: location.system.oldId ?? '',
        }
      : undefined,
  };
}

// ============================================================================
// Statistics Mappers
// ============================================================================

/**
 * Map GraphQL location statistics to LocationStats interface
 */
export function mapLocationToLocationStats(
  location: LocationDetailGraphQL | null
): LocationStats {
  if (!location) {
    return {
      total_rentals_started: 0,
      total_rentals_ended: 0,
      avg_rental_started_per_user: 0,
      avg_rental_ended_per_user: 0,
    };
  }

  return {
    total_rentals_started: location.rentalsStartedCount,
    total_rentals_ended: location.rentalsEndedCount,
    avg_rental_started_per_user: location.avgRentalsStartedPerUser ?? 0,
    avg_rental_ended_per_user: location.avgRentalsEndedPerUser ?? 0,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get display state label for location
 */
export function getLocationStateLabel(state: number): string {
  const stateMap: Record<number, string> = {
    0: 'Unassigned',
    1: 'Active',
    2: 'Inactive',
    3: 'Maintenance',
    4: 'Retired',
    5: 'Reserved',
    6: 'Rebalance',
    7: 'Missing',
    8: 'Lost',
    9: 'Stored',
    10: 'Private',
  };
  return stateMap[state] ?? 'Unknown';
}

/**
 * Get state color for UI badges
 */
export function getLocationStateColor(
  state: number
): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (state) {
    case 1: // Active
      return 'success';
    case 3: // Maintenance
      return 'warning';
    case 4: // Retired
      return 'error';
    case 0: // Unassigned
      return 'default';
    default:
      return 'info';
  }
}

/**
 * Get configuration display label
 */
export function getConfigurationLabel(configuration: string): string {
  const configMap: Record<string, string> = {
    ZONE: 'Zone',
    RACK: 'Rack',
    DOCK: 'Dock',
    STORE: 'Store',
    WAREHOUSE: 'Warehouse',
    zone: 'Zone',
    rack: 'Rack',
    dock: 'Dock',
    store: 'Store',
    warehouse: 'Warehouse',
  };
  return configMap[configuration] ?? configuration;
}

/**
 * Get zone type display label
 */
export function getZoneTypeLabel(zoneType: string): string {
  const zoneMap: Record<string, string> = {
    parking: 'Parking Zone',
    preferred: 'Preferred Parking',
    system: 'System Zone',
    restricted: 'Restricted Zone',
    slowzone: 'Slow Zone',
    nogo: 'No-Go Zone',
    deployment: 'Deployment Zone',
  };
  return zoneMap[zoneType] ?? zoneType;
}

/**
 * Check if location is a parking zone (can start/end rentals)
 */
export function isParkingZone(
  location: IStation | LocationGraphQL | LocationDetailGraphQL
): boolean {
  const zoneType = 'zone_type' in location ? location.zone_type : location.zoneType;
  const parkingTypes = ['parking', 'preferred', 'system'];
  return parkingTypes.includes(zoneType ?? '');
}

/**
 * Check if location is a restricted zone
 */
export function isRestrictedZone(
  location: IStation | LocationGraphQL | LocationDetailGraphQL
): boolean {
  const zoneType = 'zone_type' in location ? location.zone_type : location.zoneType;
  const restrictedTypes = ['restricted', 'nogo', 'slowzone'];
  return restrictedTypes.includes(zoneType ?? '');
}

/**
 * Calculate availability percentage for a location
 */
export function getAvailabilityPercentage(
  location: IStation | LocationGraphQL | LocationDetailGraphQL
): number {
  const total =
    'totalRentableHardware' in location
      ? location.totalRentableHardware
      : location.totalRentableHardwareCount;
  const available =
    'available_hardware' in location
      ? location.available_hardware
      : location.availableHardwareCount;

  if (total === 0) return 0;
  return Math.round((available / total) * 100);
}

/**
 * Format location coordinates for display
 */
export function formatLocationCoordinates(
  location: IStation | LocationGraphQL | LocationDetailGraphQL
): string {
  const lat = typeof location.lat === 'string' ? parseFloat(location.lat) : location.lat;
  const lng = typeof location.lng === 'string' ? parseFloat(location.lng) : location.lng;

  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return 'No coordinates';
  }

  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}
